/** Card model for Contract Rummy. See scope.md §3.2. */

import type { AceRule, ScoringTable } from './rules.js';
import { SCORE_TABLES } from './rules.js';

export const SUITS = ['clubs', 'diamonds', 'hearts', 'spades'] as const;
export type Suit = (typeof SUITS)[number];

export const RANKS = [
  'A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K',
] as const;
export type NaturalRank = (typeof RANKS)[number];
export type Rank = NaturalRank | 'JOKER';

export type Card = {
  readonly id: string;
  readonly rank: Rank;
  /** Jokers have no suit. */
  readonly suit?: Suit;
};

export function isJoker(card: Card): boolean {
  return card.rank === 'JOKER';
}

/**
 * Position of a rank in a run. Ace is 1 when low and 14 when high; everything else
 * is fixed. See scope.md §3.2 — Ace-low-only is the default because it makes the
 * round-7 run of thirteen exactly one whole suit.
 */
export function runOrder(rank: NaturalRank, aceHigh: boolean): number {
  if (rank === 'A') return aceHigh ? 14 : 1;
  if (rank === 'J') return 11;
  if (rank === 'Q') return 12;
  if (rank === 'K') return 13;
  return Number(rank);
}

/** Which ace interpretations a rule allows, in the order they should be tried. */
export function aceInterpretations(rule: AceRule): boolean[] {
  if (rule === 'low') return [false];
  if (rule === 'high') return [true];
  return [false, true];
}

export function rankFromRunOrder(order: number, aceHigh: boolean): NaturalRank | null {
  if (order === 1 && !aceHigh) return 'A';
  if (order === 14 && aceHigh) return 'A';
  if (order >= 2 && order <= 10) return String(order) as NaturalRank;
  if (order === 11) return 'J';
  if (order === 12) return 'Q';
  if (order === 13) return 'K';
  return null;
}

/** Penalty points for a card left in hand. scope.md §3.7. */
export function scoreOf(card: Card, table: ScoringTable): number {
  const values = SCORE_TABLES[table];
  if (card.rank === 'JOKER') return values.joker;
  if (card.rank === 'A') return values.ace;
  if (card.rank === '10' || card.rank === 'J' || card.rank === 'Q' || card.rank === 'K') {
    return values.ten;
  }
  return values.low;
}

export function scoreHand(hand: readonly Card[], table: ScoringTable): number {
  return hand.reduce((total, card) => total + scoreOf(card, table), 0);
}

/** Every pack brings two jokers. This is the game, not an option. scope.md §3.1. */
export const JOKERS_PER_PACK = 2;

/** One pack: 52 cards plus its two jokers. */
export function buildPack(packNumber: number): Card[] {
  const cards: Card[] = [];
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      cards.push({ id: `${packNumber}-${suit}-${rank}`, rank, suit });
    }
  }
  cards.push({ id: `${packNumber}-joker-a`, rank: 'JOKER' });
  cards.push({ id: `${packNumber}-joker-b`, rank: 'JOKER' });
  return cards;
}

export function buildPacks(packCount: number): Card[] {
  const cards: Card[] = [];
  for (let p = 1; p <= packCount; p += 1) cards.push(...buildPack(p));
  return cards;
}

/**
 * How much a card is worth keeping: does it pair with, or sit next to, anything else
 * in the hand? Crude, but it is the judgement a person makes at a glance, and both
 * the bots and the greedy driver need it — a player who discards at random never
 * assembles a contract and the round never ends.
 */
export function keepScore(card: Card, hand: readonly Card[]): number {
  if (isJoker(card)) return 100;
  let score = 0;
  for (const other of hand) {
    if (other.id === card.id || isJoker(other)) continue;
    if (other.rank === card.rank) score += 3;
    if (other.suit === card.suit) {
      const gap = Math.abs(
        runOrder(other.rank as NaturalRank, false) - runOrder(card.rank as NaturalRank, false),
      );
      if (gap === 1) score += 3;
      else if (gap === 2) score += 1;
    }
  }
  return score;
}

/** The card a player would least mind losing. */
export function leastUseful(hand: readonly Card[]): Card | undefined {
  return hand.slice().sort((a, b) => keepScore(a, hand) - keepScore(b, hand))[0];
}
